import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  styled,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import CInput from "../../../../components/CInput";
import CAutocomplete from "../../../../components/CAutocomplete";
import { getApiData } from "../../../../utils/APIHelper";
import {
  materialUnitOptions,
  supplyApplyOptions,
  boqTypeOptions,
  normalizeSupplyApply,
  requiresLeadTime,
} from "../../../../constants/boq";

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
  "&:hover": {
    borderColor: "#4D6BF6",
    color: "#ffffff",
    backgroundColor: "#4D6BF6",
  },
  "&:disabled": {
    opacity: 0.7,
    borderColor: "#4D6BF6",
    color: "#ffffff",
    backgroundColor: "#4D6BF6",
    cursor: "not-allowed",
  },
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: "16px",
  marginTop: "8px",
}));

const StyledFormControlLabel = styled(FormControlLabel)(
  ({ theme, checked }) => ({
    margin: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: "14px",
      fontWeight: 500,
      padding: "8px 16px",
      border: "1px solid #D7D8D9",
      borderRadius: "8px",
      backgroundColor: checked ? "#4D6BF6" : "#fff",
      color: checked ? "#fff" : "#666",
      transition: "all 0.2s ease",
      cursor: "pointer",
      minWidth: "120px",
      textAlign: "center",
    },
    "&.Mui-checked .MuiFormControlLabel-label": {
      backgroundColor: "#4D6BF6",
      color: "#fff",
      borderColor: "#4D6BF6",
    },
  })
);

const AddItemModal = ({
  isOpen,
  onClose,
  inclusionName,
  onSubmit,
  loading = false,
  isEditing = false,
  editingBoq = null,
  onDeleteImage = null,
  onUploadImage = null,
  rooms = [],
  selectedRoom = null,
  onRoomChange = null,
  project = null,
  roomNameForSubcategories = "",
  isSiteVisit = false,
}) => {
  const [formData, setFormData] = useState({
    item_name: "",
    subcategory: "",
    supply_apply: "",
    quantity: "",
    days: "",
    material_unit: "",
    specification: "",
    boq_type: "Materials",
    image: null,
    unit_cost: "",
    lead_time: "",
  });

  const [errors, setErrors] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [boqImageData, setBoqImageData] = useState(null);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [subcategoriesFetched, setSubcategoriesFetched] = useState(false);

  // Initialize form when editing
  useEffect(() => {
    if (isOpen && isEditing && editingBoq) {
      const editBoqType = editingBoq.type || "Materials";
      const existingUnitCostRaw =
        editBoqType === "Activities"
          ? editingBoq.manpower_rate
          : editingBoq.unit_cost;
      // Existing BOQs may have lowercase "supply"/"apply" — coerce to the
      // canonical capitalized form before seeding the dropdown so the
      // option matches and the lead-time toggle fires correctly.
      const normalizedSupplyApply =
        normalizeSupplyApply(
          editingBoq.supply_type || editingBoq.supply_apply,
        ) || "";
      setFormData({
        item_name: editingBoq.item_name || "",
        subcategory: editingBoq.subcategory || "",
        supply_apply: normalizedSupplyApply,
        quantity:
          editBoqType === "Activities"
            ? ""
            : editingBoq.quantity != null
            ? String(editingBoq.quantity)
            : "",
        days:
          editBoqType === "Activities"
            ? editingBoq.days != null
              ? String(editingBoq.days)
              : ""
            : "",
        material_unit:
          editBoqType === "Activities"
            ? ""
            : editingBoq.unit_of_measurement || editingBoq.material_unit || "",
        specification: editingBoq.specification || "",
        boq_type: editBoqType,
        image: null,
        unit_cost:
          existingUnitCostRaw != null ? String(existingUnitCostRaw) : "",
        lead_time:
          editingBoq.lead_time != null ? String(editingBoq.lead_time) : "",
      });
      setSelectedImage(null);
      setErrors({});
    } else if (isOpen && !isEditing) {
      // Reset form when opening for new item
      setFormData({
        item_name: "",
        subcategory: "",
        supply_apply: "",
        quantity: "",
        days: "",
        material_unit: "",
        specification: "",
        boq_type: "Materials",
        image: null,
        unit_cost: "",
        lead_time: "",
      });
      setErrors({});
      setSelectedImage(null);
      setImagePreview(null);
      setBoqImageData(null);
    }
  }, [isOpen, isEditing, editingBoq]);

  // Clear days/quantity/material_unit when boq_type toggles (matches portal)
  const skipFirstBoqTypeEffect = React.useRef(true);
  useEffect(() => {
    if (skipFirstBoqTypeEffect.current) {
      skipFirstBoqTypeEffect.current = false;
      return;
    }
    setFormData((prev) => {
      if (prev.boq_type === "Materials") {
        return { ...prev, days: "" };
      }
      return { ...prev, quantity: "", material_unit: "" };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.days;
      delete next.quantity;
      delete next.material_unit;
      return next;
    });
  }, [formData.boq_type]);

  // Fetch BOQ image when editing (matches portal AddItemModal)
  useEffect(() => {
    const fetchBoqImage = async () => {
      if (isOpen && isEditing && editingBoq?.id) {
        try {
          const response = await getApiData(
            `api/project/files/${editingBoq.id}?type=budgetItemDetails`,
            "GET",
            {}
          );
          if (response?.success && response.data && response.data.length > 0) {
            const imageData = response.data[0];
            setBoqImageData(imageData);
            const imageUrl = imageData.path
              ? imageData.path.startsWith("http")
                ? imageData.path
                : `https://renohome.blob.core.windows.net/${imageData.path}`
              : imageData.image || null;
            setImagePreview(imageUrl);
          } else {
            setBoqImageData(null);
            setImagePreview(null);
          }
        } catch (error) {
          console.error("Error fetching BOQ image:", error);
          setBoqImageData(null);
          setImagePreview(null);
        }
      }
    };

    if (isOpen && isEditing && editingBoq?.id) {
      fetchBoqImage();
    }
  }, [isOpen, isEditing, editingBoq?.id]);

  // Update selectedRoom when rooms prop changes (for By Category tab)
  useEffect(() => {
    if (isOpen && !isEditing && rooms && rooms.length > 0 && selectedRoom) {
      // Ensure selectedRoom matches one of the available rooms
      const matchingRoom = rooms.find(
        (r) => (r.id || r.room_id) === (selectedRoom.id || selectedRoom.value || selectedRoom.room_id)
      );
      if (!matchingRoom && onRoomChange) {
        // Reset selection if room doesn't match
        onRoomChange(null);
      }
    }
  }, [isOpen, rooms, selectedRoom, isEditing, onRoomChange]);

  // Fetch subcategories from API (matches portal AddItemModal)
  const fetchSubcategories = useCallback(
    async (categoryName, normalizedRoomName = "") => {
      if (!categoryName) return;
      try {
        setLoadingSubcategories(true);
        const roomParam =
          normalizedRoomName && String(normalizedRoomName).trim() !== ""
            ? `&room_name=${encodeURIComponent(String(normalizedRoomName).trim())}`
            : "";
        const projectType = project?.project_type || "";
        const endpoint = `api/rooms/inclusions/subcategories?category=${encodeURIComponent(
          categoryName
        )}&project_type=${encodeURIComponent(projectType)}${roomParam}`;
        const data = await getApiData(endpoint, "GET", {});
        if (data?.success && data.subcategories) {
          const options = data.subcategories.map((item) => ({
            label: item.subcategory_name,
            value: item.subcategory_name,
          }));
          setSubcategoryOptions(options);
        } else {
          setSubcategoryOptions([]);
        }
        setSubcategoriesFetched(true);
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        setSubcategoryOptions([]);
        setSubcategoriesFetched(true);
      } finally {
        setLoadingSubcategories(false);
      }
    },
    [project?.project_type]
  );

  const handleSubcategoryDropdownOpen = () => {
    if (!subcategoriesFetched && inclusionName && !loadingSubcategories) {
      if (isSiteVisit && !roomNameForSubcategories) return;
      fetchSubcategories(inclusionName, roomNameForSubcategories);
    }
  };

  // Reset fetched state when modal opens so subcategories refetch per session
  useEffect(() => {
    if (isOpen) {
      setSubcategoriesFetched(false);
      setSubcategoryOptions([]);
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // If editing and onUploadImage is provided, upload immediately
      if (isEditing && onUploadImage) {
        try {
          await onUploadImage(file);
          // Refresh preview after upload
          const reader = new FileReader();
          reader.onloadend = () => {
            setImagePreview(reader.result);
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error("Error uploading image:", error);
        }
      } else {
        // For new items, just set the file
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.item_name.trim()) {
      newErrors.item_name = "Item name is required";
    }

    if (!formData.boq_type) {
      newErrors.boq_type = "Item Type is required";
    }

    if (!formData.subcategory) {
      newErrors.subcategory = "Subcategory is required";
    }

    if (!formData.supply_apply) {
      newErrors.supply_apply = "Supply/Apply is required";
    }

    if (formData.boq_type === "Materials") {
      if (!formData.material_unit) {
        newErrors.material_unit = "Material Unit is required";
      }
      if (!formData.quantity || formData.quantity <= 0) {
        newErrors.quantity = "Quantity must be greater than 0";
      }
    } else if (formData.boq_type === "Activities") {
      if (!formData.days || formData.days <= 0) {
        newErrors.days = "Days must be greater than 0";
      }
    }

    if (!formData.specification.trim()) {
      newErrors.specification = "Specification is required";
    }

    // Lead Time — mandatory in reno-web (contractor) whenever supply/apply
    // is "Supply" or "Supply and Apply". Optional in portal; here we
    // require it AND require a positive integer.
    if (requiresLeadTime(formData.supply_apply)) {
      const trimmedLT = String(formData.lead_time ?? "").trim();
      if (trimmedLT === "") {
        newErrors.lead_time = "Lead Time is required";
      } else if (!/^\d+$/.test(trimmedLT)) {
        newErrors.lead_time = "Lead Time must be a positive integer";
      }
    }

    if (isEditing) {
      const trimmedUnitCost = String(formData.unit_cost ?? "").trim();
      if (trimmedUnitCost !== "" && Number(trimmedUnitCost) < 0) {
        newErrors.unit_cost = "Unit cost must be 0 or greater";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (validateForm()) {
      // supply_apply is already canonical (we normalize on edit-init and
      // the dropdown values are capitalized). Coerce lead_time to int
      // when applicable; otherwise null so backend stores nothing.
      const needsLeadTime = requiresLeadTime(formData.supply_apply);
      const leadTimeTrimmed = String(formData.lead_time ?? "").trim();
      const leadTimeOut =
        needsLeadTime && leadTimeTrimmed !== ""
          ? parseInt(leadTimeTrimmed, 10)
          : null;

      const submitData = {
        item_name: formData.item_name,
        boq_type: formData.boq_type,
        subcategory: formData.subcategory,
        supply_apply: formData.supply_apply,
        specification: formData.specification,
        material_unit:
          formData.boq_type === "Activities" ? null : formData.material_unit,
        quantity: formData.boq_type === "Materials" ? formData.quantity : "",
        days: formData.boq_type === "Activities" ? formData.days : "",
        image: selectedImage,
        room_id: selectedRoom ? (selectedRoom.id || selectedRoom.value) : null,
        lead_time: leadTimeOut,
        unit_cost: isEditing
          ? formData.unit_cost == null
            ? ""
            : String(formData.unit_cost).trim()
          : undefined,
      };

      await onSubmit(submitData);

      // Reset form after successful submission (only if not editing)
      if (!isEditing) {
        setFormData({
          item_name: "",
          subcategory: "",
          supply_apply: "",
          quantity: "",
          days: "",
          material_unit: "",
          specification: "",
          boq_type: "Materials",
          image: null,
          unit_cost: "",
          lead_time: "",
        });
        setErrors({});
        setSelectedImage(null);
        setImagePreview(null);
      }
    }
  };

  const handleClose = () => {
    // Reset form to default values
    setFormData({
      item_name: "",
      subcategory: "",
      supply_apply: "",
      quantity: "",
      days: "",
      material_unit: "",
      specification: "",
      boq_type: "Materials",
      image: null,
      unit_cost: "",
      lead_time: "",
    });
    setErrors({});
    setSelectedImage(null);
    setImagePreview(null);
    setBoqImageData(null);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          maxHeight: "90vh",
          overflowX: "clip",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "24px",
          fontWeight: 600,
          color: "#0D0D0E",
          paddingBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <div style={{ fontSize: "16px", fontWeight: 600, letterSpacing: 0 }}>
            {isEditing ? "Edit Item" : `Add Item to ${inclusionName}`}
          </div>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: "0 1.5vw" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* BOQ Type Radio Buttons */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Item Type
            </Typography>
            <FormControl component="fieldset" error={!!errors.boq_type}>
              <StyledRadioGroup
                value={formData.boq_type}
                onChange={(e) => handleInputChange("boq_type", e.target.value)}
                row
                aria-label="boq_type"
              >
                {boqTypeOptions.map((option) => (
                  <StyledFormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio sx={{ display: "none" }} />}
                    label={option.label}
                    checked={formData.boq_type === option.value}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        backgroundColor:
                          formData.boq_type === option.value
                            ? "#4D6BF6"
                            : "#fff",
                        color:
                          formData.boq_type === option.value ? "#fff" : "#666",
                        borderColor:
                          formData.boq_type === option.value
                            ? "#4D6BF6"
                            : "#D7D8D9",
                      },
                    }}
                  />
                ))}
              </StyledRadioGroup>
              {errors.boq_type && (
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", mt: 1 }}
                >
                  {errors.boq_type}
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* Room Selection (only for By Category tab when adding new item) */}
          {!isEditing && rooms && rooms.length > 0 && onRoomChange && (
            <CAutocomplete
              label="Room"
              placeholder="Select room"
              value={selectedRoom ? rooms.find((r) => (r.id || r.room_id) === (selectedRoom.id || selectedRoom.room_id)) : null}
              onChange={(e, newValue) => {
                if (onRoomChange) {
                  onRoomChange(newValue);
                }
              }}
              options={rooms.map((room) => ({
                id: room.id || room.room_id,
                name: room.name || room.room_name,
                label: room.name || room.room_name,
                value: room.id || room.room_id,
              }))}
              getOptionLabel={(option) => option.name || option.label || option}
            />
          )}

          {/* Item Name */}
          <CInput
            label="Item Name"
            placeholder="Enter Item Name"
            value={formData.item_name}
            onChange={(e) => handleInputChange("item_name", e.target.value)}
            error={!!errors.item_name}
            helpertext={errors.item_name}
          />

          {/* Subcategory and Supply/Apply in same row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
            }}
          >
            <CAutocomplete
              label="Subcategory"
              placeholder={loadingSubcategories ? "Loading subcategories..." : "Select subcategory"}
              value={
                formData.subcategory
                  ? subcategoryOptions.find(
                      (option) => option.value === formData.subcategory
                    ) || { label: formData.subcategory, value: formData.subcategory }
                  : null
              }
              onChange={(e, newValue) => {
                handleInputChange("subcategory", newValue?.value || "");
              }}
              options={
                formData.subcategory &&
                !subcategoryOptions.some((o) => o.value === formData.subcategory)
                  ? [
                      { label: formData.subcategory, value: formData.subcategory },
                      ...subcategoryOptions,
                    ]
                  : subcategoryOptions
              }
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, val) => option?.value === val?.value}
              onOpen={handleSubcategoryDropdownOpen}
              loading={loadingSubcategories}
              error={!!errors.subcategory}
              helpertext={errors.subcategory}
            />

            <CAutocomplete
              label="Supply/Apply"
              placeholder="Select supply/apply"
              value={supplyApplyOptions.find(
                (option) => option.value === formData.supply_apply
              )}
              onChange={(e, newValue) => {
                const nextVal = newValue?.value || "";
                setFormData((prev) => ({
                  ...prev,
                  supply_apply: nextVal,
                  // Clear lead_time when switching to a value that does
                  // not require it — otherwise stale "Apply"-time data
                  // would tag along into the payload as null anyway, but
                  // the field would still show its old value.
                  lead_time: requiresLeadTime(nextVal) ? prev.lead_time : "",
                }));
                if (errors.supply_apply || errors.lead_time) {
                  setErrors((prev) => ({
                    ...prev,
                    supply_apply: "",
                    lead_time: "",
                  }));
                }
              }}
              options={supplyApplyOptions}
              getOptionLabel={(option) => option.label}
              error={!!errors.supply_apply}
              helpertext={errors.supply_apply}
            />
          </Box>

          {/* Lead Time — shown only when Supply or Supply and Apply.
              Mandatory in reno-web (contractor) per validateForm. */}
          {requiresLeadTime(formData.supply_apply) && (
            <CInput
              label="Lead Time (days)"
              placeholder="Enter Lead Time in days"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              value={formData.lead_time}
              onChange={(e) => {
                // Strip non-digits so we never have to fix it on submit
                const v = (e.target.value || "").replace(/[^0-9]/g, "");
                handleInputChange("lead_time", v);
              }}
              error={!!errors.lead_time}
              helpertext={errors.lead_time}
            />
          )}

          {/* Quantity/Days (always) + Material Unit (Materials only) in same row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
            }}
          >
            {formData.boq_type === "Materials" ? (
              <CInput
                label="Quantity"
                type="number"
                placeholder="Enter Quantity"
                value={formData.quantity}
                onChange={(e) =>
                  handleInputChange("quantity", parseFloat(e.target.value) || "")
                }
                error={!!errors.quantity}
                helpertext={errors.quantity}
              />
            ) : (
              <CInput
                label="Days"
                type="number"
                placeholder="Enter Days"
                value={formData.days}
                onChange={(e) =>
                  handleInputChange("days", parseFloat(e.target.value) || "")
                }
                error={!!errors.days}
                helpertext={errors.days}
              />
            )}

            {formData.boq_type === "Materials" && (
              <CAutocomplete
                label="Material Unit"
                placeholder="Select material unit"
                value={materialUnitOptions.find(
                  (option) => option.value === formData.material_unit
                )}
                onChange={(e, newValue) => {
                  handleInputChange("material_unit", newValue?.value || "");
                }}
                options={materialUnitOptions}
                getOptionLabel={(option) => option.label}
                error={!!errors.material_unit}
                helpertext={errors.material_unit}
              />
            )}
          </Box>

          {/* Unit Cost — edit mode only (matches portal AddItemModal) */}
          {isEditing && (
            <CInput
              label="Unit Cost"
              type="number"
              placeholder="Enter Unit Cost"
              value={formData.unit_cost}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || Number(v) >= 0) {
                  handleInputChange("unit_cost", v);
                }
              }}
              inputProps={{ min: 0, step: 1 }}
              error={!!errors.unit_cost}
              helpertext={errors.unit_cost}
            />
          )}

          {/* Image Upload Section */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Item Image (Optional)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              {imagePreview && (
                <Box
                  sx={{
                    position: "relative",
                    width: "64px",
                    height: "64px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #D7D8D9",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {isEditing && (boqImageData?.id || (editingBoq?.image && editingBoq.image.length > 0)) && onDeleteImage && (
                    <Button
                      onClick={() => {
                        const imageId = boqImageData?.id || editingBoq.image?.[0]?.id;
                        if (imageId && onDeleteImage) {
                          onDeleteImage(imageId);
                          setBoqImageData(null);
                          setImagePreview(null);
                        }
                      }}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        minWidth: "auto",
                        width: "20px",
                        height: "20px",
                        backgroundColor: "#DC2626",
                        color: "#fff",
                        padding: 0,
                        "&:hover": {
                          backgroundColor: "#B91C1C",
                        },
                      }}
                    >
                      ×
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Specification */}
          <CInput
            label="Specifications"
            placeholder="Write specification here..."
            multiline
            rows={4}
            value={formData.specification}
            onChange={(e) => handleInputChange("specification", e.target.value)}
            error={!!errors.specification}
            helpertext={errors.specification}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "space-between" }}
      >
        <GreyStyledButton onClick={handleClose} disabled={loading}>
          Cancel
        </GreyStyledButton>
        <StyledButton onClick={handleFormSubmit} disabled={loading}>
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              {isEditing ? "Updating..." : "Adding..."}
            </>
          ) : (
            isEditing ? "Update Item" : "Add Item"
          )}
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddItemModal;
